import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { providerTabs } from "./constants";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import { RowContainer } from "../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { darkGreen, greyText, lightGreyOutline } from "../../../styles";
import { Coverage, Provider } from "../../../models/CompanyprofileModels";
import { LoadingTableCell } from "../../../components/tables/LoadingTableCell";

const TabContainer = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${lightGreyOutline};
  border-radius: 16px;
  margin-right: 12px;
  cursor: pointer;
  background-color: ${({ isActive }): string =>
    isActive ? "#f8f8f8" : "transparent"};
`;

export const ProviderTab = ({
  handleSetActiveTab,
  coverage,
  isLoading = false,
}: ProviderTabProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<Provider | null>(null);
  const [allowedProviders, setAllowedProviders] = useState<Provider[]>([]);
  const [hoveredTab, setHoveredTab] = useState<Provider | null>(null);

  const handleTabClick = (tab: Provider): void => {
    setActiveTab(tab);

    handleSetActiveTab && handleSetActiveTab(tab);
  };

  const getAllowedProviders = (
    coverage: Record<Provider, Coverage>
  ): Provider[] => {
    const providerTabsKeys = Object.keys(providerTabs) as Provider[];
    return providerTabsKeys.filter((provider) => coverage[provider].isCovered);
  };

  useEffect(() => {
    setAllowedProviders(getAllowedProviders(coverage));
  }, [coverage]);

  useEffect(() => {
    if (!activeTab && allowedProviders.length > 0) {
      const defaultTab = allowedProviders[0] as Provider;

      handleTabClick(defaultTab);
    }
  }, [allowedProviders]);

  return (
    <RowContainer jc="flex-start" ai="center">
      {allowedProviders.map((provider) => {
        const isActive = provider === activeTab || provider === hoveredTab;
        const { icon, iconSize, iconActive, title } =
          providerTabs[provider as keyof typeof providerTabs];
        return isLoading ? (
          <LoadingTableCell key={provider} dark={true} ml={1} />
        ) : (
          <TabContainer
            key={provider}
            isActive={isActive}
            onMouseEnter={(): void => setHoveredTab(provider as Provider)}
            onMouseLeave={(): void => setHoveredTab(null)}
            onClick={(): void => handleTabClick(provider as Provider)}
          >
            <IconContainer
              key={provider}
              size={iconSize || 32}
              cursor="pointer"
            >
              <Icon src={isActive ? iconActive : icon} alt={title} />
            </IconContainer>
            <CompassParagraph
              fontSize={12}
              color={isActive ? darkGreen : greyText}
              cursor="pointer"
            >
              {title}
            </CompassParagraph>
          </TabContainer>
        );
      })}
    </RowContainer>
  );
};

type ProviderTabProps = {
  coverage: Record<Provider, Coverage>;
  handleSetActiveTab?: (tab: Provider) => void;
  isLoading?: boolean;
};
